import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    if (this.dataset.megaMenuPinned === 'true' && this.dataset.megaMenuPinIndex) {
      onDocumentLoaded(() => this.#initPinnedMegaMenu());
    }
  }

  /**
   * When the theme pins the first mega menu open, keep height/ARIA in sync and avoid closing on pointer leave.
   * @type {HTMLElement | null}
   */
  #pinnedMenuItem = null;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Initialize pinned mega menu (theme editor / design preview).
   */
  #initPinnedMegaMenu = () => {
    const idx = this.dataset.megaMenuPinIndex;
    if (!idx) return;

    const inner = this.querySelector(`#submenu-${idx}`);
    const submenu = inner?.parentElement;
    const item = inner?.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');

    if (
      !(submenu instanceof HTMLElement) ||
      !submenu.classList.contains('menu-list__submenu') ||
      !(item instanceof HTMLElement)
    ) {
      return;
    }

    this.#pinnedMenuItem = item;
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    submenu.dataset.active = '';

    const submenuHeight = submenu.offsetHeight;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) delete prevSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    // on:pointerleave on the <li> also runs when leaving a child (e.g. a flyout link).
    // Ignore those; only close when the pointer leaves the whole item (nav + panel).
    const listItem = findMenuListItem(event.target);
    const { relatedTarget } = event;
    if (relatedTarget instanceof Node && listItem?.contains(relatedTarget)) {
      return;
    }

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.dataset.megaMenuPinned === 'true' && item === this.#pinnedMenuItem) {
      return;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuListItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.classList.contains('menu-list__list-item')) {
    return element instanceof HTMLElement ? element : null;
  }

  const li = element.closest('.menu-list__list-item');
  return li instanceof HTMLElement ? li : null;
}

/**
 * Find the menu item anchor for events.
 * Note: delegated pointerenter sets event.target to the element with on:pointerenter (often the <li>),
 * so we must look inside the li as well as walking ancestors.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) return element;

  const ancestor = element.closest('[ref="menuitem"]');
  if (ancestor instanceof HTMLElement) return ancestor;

  const fromLi = element.querySelector?.('[ref="menuitem"]');
  return fromLi instanceof HTMLElement ? fromLi : null;
}

/**
 * Find the flyout submenu panel for a menu item anchor.
 * @param {Element | null | undefined} element - Usually the menuitem anchor
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const li = element?.closest('.menu-list__list-item');
  if (!li) return null;

  for (const el of li.querySelectorAll('[ref]')) {
    if (el.getAttribute('ref') === 'submenu[]') {
      return el instanceof HTMLElement ? el : null;
    }
  }

  return null;
}
